import { Component, DestroyRef, ElementRef, Inject, OnInit, signal, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { WaypointService } from 'src/app/services/core/waypoint.service';
import { MapService } from 'src/app/services/map/map.service';
import { AccountService } from 'src/app/services/account/account.service';
import { SettingsService, MapSkin, Unit } from 'src/app/services/account/settings.service';
import { LanguageService } from 'src/app/services/account/language.service';
import { ConsoleLoggerService } from 'src/app/services/logging/console-logger.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MapElementType } from 'src/app/models/map-element-type';
import { UnitSettings } from 'src/app/models/unit-settings';
import { VoiceGuidenceSettings } from 'src/app/models/voice-guidence-settings';
import { MapSettings } from 'src/app/models/map-settings';
import { EvSwitchComponent } from '../../ui-components/ev-switch/ev-switch.component';
import { CommonModule } from '@angular/common';
import { MatFormField, MatOption, MatSelect } from '@angular/material/select';
import { EvLoaderComponent } from '../../ui-components/ev-loader/ev-loader.component';
import { ModalOptionPanelComponent } from '../modal-option-panel/modal-option-panel.component';
import { StackedModalsService } from 'src/app/services/modal/stacked-modals.service';
import { AnalyticsConsentService } from 'src/app/services/platform/analytics-consent.service';

@Component({
  standalone: true,
  selector: 'app-settings-dialog',
  templateUrl: './settings-dialog.component.html',
  imports: [EvSwitchComponent, CommonModule, MatOption, MatSelect, MatFormField, EvLoaderComponent, ModalOptionPanelComponent],
  styleUrls: ['./settings-dialog.component.scss']
})
export class SettingsDialogComponent implements OnInit {

  @ViewChild('settingsScrollbar') settingsScrollbar?: ElementRef<HTMLElement>;

  /**
   * Every value the template renders is a signal: HTTP responses are delivered outside the
   * Angular zone, so a signal write is what notifies change detection that they changed.
   */
  public readonly title = signal<string>("");
  public readonly Language = signal<{ code: string, name: string }[]>([]);

  public readonly settingValueVoiceGuidance = signal<number>(1);
  public readonly settingValueMapColors = signal<number>(0);
  public readonly settingValuePOIs = signal<number>(0);
  public readonly settingValueDistance = signal<number>(1);
  public readonly settingValueTemperature = signal<number>(1);
  public readonly settingValueSpeed = signal<number>(1);
  public readonly settingValuePressure = signal<number>(1);
  public readonly settingValueLanguage = signal<string>("");
  public readonly settingValueAnalytics = signal<number>(0);

  public readonly dialogContentIsLoading = signal<boolean>(false);
  public readonly settingsError = signal<boolean>(false);

  public readonly homeAddressText = signal<string>('');
  public readonly workAddressText = signal<string>('');

  public readonly analyticsConsentService = inject(AnalyticsConsentService);

  constructor(
    public dialogRef: MatDialogRef<SettingsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public mapService: MapService,
    private readonly waypointService: WaypointService,
    private readonly destroyRef: DestroyRef,
    private readonly stackedModalsService: StackedModalsService,
    public accountService: AccountService,
    public settingsService: SettingsService,
    public languageService: LanguageService,
    private readonly consoleLoggerService: ConsoleLoggerService
  ) { }

  ngOnInit(): void {
    this.Language.set(this.languageService.getLanguageList());

    const unit: UnitSettings = this.settingsService.getUnit();
    this.settingValueDistance.set((unit.Distance === Unit.Metric ? 1 : 0));
    this.settingValueTemperature.set((unit.Temperature === Unit.Metric ? 1 : 0));
    this.settingValueSpeed.set((unit.Speed === Unit.Metric ? 1 : 0));
    this.settingValuePressure.set((unit.Pressure === Unit.Metric ? 1 : 0));

    const voiceguidance: VoiceGuidenceSettings = this.settingsService.getVoiceGuidence();
    this.settingValueVoiceGuidance.set((voiceguidance.Mute ? 0 : 1));

    const mapsettings: MapSettings = this.settingsService.getMap();
    this.settingValueMapColors.set((mapsettings.Skin === MapSkin.Light ? 1 : 0));
    this.settingValuePOIs.set((mapsettings.POIs ? 1 : 0));

    this.dialogRef.backdropClick().subscribe(() => this.dialogClose());

    this.settingValueLanguage.set(this.languageService.getLanguage());
    this.settingValueAnalytics.set(this.analyticsConsentService.consent() === 'granted' ? 1 : 0);

    // Keep the title (and the rest of the modal) in sync with the loaded language.
    // Fires immediately with the current language and again after each switch completes.
    this.languageService.activeLanguage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.initTitle());

    this.initAddresses();
  }

  private initTitle(): void {
    this.title.set(this.languageService.languageJSON.Global_Settings);
  }

  private initAddresses(): void {
    const homeAddress = this.settingsService.getHomeAddress();
    if (homeAddress) this.homeAddressText.set(homeAddress.Description);
    const workAddress = this.settingsService.getWorkAddress();
    if (workAddress) this.workAddressText.set(workAddress.Description);
  }

  public switchParamsChanged(): void {
    this.dialogContentIsLoading.set(true);

    const unit: any = {
      distance: this.settingValueDistance() > 0 ? 'metric' : 'imperial',
      temperature: this.settingValueTemperature() > 0 ? 'metric' : 'imperial',
      speed: this.settingValueSpeed() > 0 ? 'metric' : 'imperial',
      pressure: this.settingValuePressure() > 0 ? 'metric' : 'imperial'
    };
    this.settingsService.setUnit(this.settingsService.unitJSONtoUnitSettings(unit));

    const voiceguidance: any = { mute: this.settingValueVoiceGuidance() == 0 };

    const mapsettings: any = {
      skin: this.settingValueMapColors() > 0 ? MapSkin.Light : MapSkin.Dark,
      pois: this.settingValuePOIs() > 0
    };
    this.settingsService.setVoiceGuidence(this.settingsService.voiceGuidenceJSONtoVoiceGuidenceSettings(voiceguidance));
    if (this.settingsService.getMap().Skin != mapsettings.skin || this.settingsService.getMap().POIs != mapsettings.pois) {
      this.settingsService.setMap(this.settingsService.mapJSONtoMapSettings(mapsettings));
    }

    this.dialogContentIsLoading.set(false);

  }

  /** Applies at once, like the other switches; it is the visitor's own choice, not a server-side setting. */
  public analyticsChanged(): void {
    this.analyticsConsentService.setConsent(this.settingValueAnalytics() > 0);
  }

  public editAddress(addressType: string): void {
    this.mapService.ObservableShowOnMap.next({ type: MapElementType.Address, data: addressType });
    this.dialogRef.close();
  }

  public removeAddress(addressType: string): void {
    const dialog = this.stackedModalsService.openModalWindow(ConfirmationDialogComponent, {
      data: {
        title: this.languageService.languageJSON.SettingsDialog_DeleteAddress_Title,
        message: addressType === 'home'
          ? this.languageService.languageJSON.SettingsDialog_DeleteAddress_Desc_Home
          : this.languageService.languageJSON.SettingsDialog_DeleteAddress_Desc_Work,
        buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
      }, autoFocus: false
    });

    dialog.afterClosed().subscribe((resp) => {
      if (!resp) return;
      if (addressType === 'home') {
        this.settingsService.removeHomeAddress();
        this.homeAddressText.set('');
      }
      if (addressType === 'work') {
        this.settingsService.removeWorkAddress();
        this.workAddressText.set('');
      }
      this.accountService.setUserAddresses().subscribe();
    });
  }

  public languageChanged(): void {
    this.languageService.setLanguage(this.settingValueLanguage());
  }

  public dialogClose(): void {
    if (this.accountService.getIsAuthorized()) {
      this.settingsError.set(false);
      this.dialogContentIsLoading.set(true);
      this.accountService.setUserSettings().subscribe({
        next: () => this.dialogRef.close(),
        error: (error) => {
          this.dialogContentIsLoading.set(false);
          if (error?.name === 'TimeoutError') {
            this.settingsError.set(true);
            this.settingsScrollbar?.nativeElement?.scrollTo({ top: this.settingsScrollbar.nativeElement.scrollHeight, behavior: 'auto' });
          } else if (error?.status === 401) {
            this.dialogRef.close();
          }
        }
      });
    } else {
      this.dialogRef.close();
    }
  }
}
